import React from "react";
import { Modal } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import PlaceBetOpenBets from "./PlaceBetOpenBets";
import { fetchRunPositionAsync } from "../../store/slices/gameSlice";
import { toast } from "react-toastify";
import { toggleFavoriteMarket } from "../../store/slices/sportEventSlice";
import { fetchMultiMarketFancy } from "../../store/slices/multiMarketSlice";

const useFlashOnChange = (value, duration = 400) => {
  const [flash, setFlash] = React.useState(false);
  const prevRef = React.useRef(value);

  React.useEffect(() => {
    const prev = prevRef.current;
    const isSame = JSON.stringify(prev) === JSON.stringify(value);
    if (isSame) return;

    prevRef.current = value;
    setFlash(true);
    const id = setTimeout(() => setFlash(false), duration);
    return () => clearTimeout(id);
  }, [value, duration]);

  return flash;
};

/**
 * Renders a single fancy market row (lay/back odds).
 * @param {{ id: string, label: string, labelSuffix?: string, lay?: number|string, laySize?: number|string, back: number|string, backSize: number|string, maxBet: number|string, maxMarket: string, backOnly?: boolean }} item
 */
const FancyDetailRow = ({
  item,
  onOddsClick,
  matchId,
  marketId,
  initialBookmarked,
}) => {
  const {
    id,
    label,
    labelSuffix,
    lay,
    laySize,
    back,
    backSize,
    minStake,
    maxStake,
    backOnly = false,
    gameStatus = "",
    isSuspended = false,
    liability = 0,
    fancyId = null,
    selectionId = null,
  } = item;
  const dispatch = useDispatch();
  const selectedBet = useSelector((state) => state.game.selectedBet);
  const [showBookModal, setShowBookModal] = React.useState(false);
  const [bookLoading, setBookLoading] = React.useState(false);
  const [bookError, setBookError] = React.useState("");
  const [bookData, setBookData] = React.useState(null);

  const flash = useFlashOnChange({ lay, laySize, back, backSize });
  const isSelectedFancyRow =
    !!selectedBet &&
    !!selectedBet?.isFancy &&
    String(selectedBet?.fancyId) === String(id);

  const [isFancyBookmarkedState, setIsFancyBookmarkedState] = React.useState(
    () => {
      if (typeof initialBookmarked === "boolean") return initialBookmarked;
      const v =
        item?.is_favourite ?? item?.is_favorite ?? item?.favorite ?? false;
      return Boolean(Number(v) || v === true);
    },
  );
  const [pendingFancyBookmarkValue, setPendingFancyBookmarkValue] =
    React.useState(null);

  React.useEffect(() => {
    if (pendingFancyBookmarkValue !== null) return;

    if (typeof initialBookmarked === "boolean") {
      setIsFancyBookmarkedState(initialBookmarked);
      return;
    }
    const v =
      item?.is_favourite ?? item?.is_favorite ?? item?.favorite ?? false;
    setIsFancyBookmarkedState(Boolean(Number(v) || v === true));
  }, [initialBookmarked, item, pendingFancyBookmarkValue]);

  React.useEffect(() => {
    if (pendingFancyBookmarkValue === null) return;

    const serverRaw =
      typeof initialBookmarked === "boolean"
        ? initialBookmarked
        : (item?.is_favourite ?? item?.is_favorite ?? item?.favorite ?? false);
    const serverValue = Boolean(Number(serverRaw) || serverRaw === true);

    if (serverValue === pendingFancyBookmarkValue) {
      setPendingFancyBookmarkValue(null);
      setIsFancyBookmarkedState(serverValue);
    }
  }, [initialBookmarked, item, pendingFancyBookmarkValue]);

  const handleFancyBookmarkClick = (e) => {
    if (e?.preventDefault) e.preventDefault();
    if (matchId == null || (fancyId == null && id == null)) return;
    const next = !isFancyBookmarkedState;
    setPendingFancyBookmarkValue(next);
    setIsFancyBookmarkedState(next);

    dispatch(
      toggleFavoriteMarket({
        eventId: fancyId ?? id,
        matchId: String(matchId),
        isAdd: next,
        isFancy: true,
      }),
    )
      .unwrap()
      .then(() => {
        dispatch(fetchMultiMarketFancy());
        toast.success(next ? "Added to favorites" : "Removed from favorites");
      })
      .catch((err) => {
        setPendingFancyBookmarkValue(null);
        setIsFancyBookmarkedState((prev) => !prev);
        toast.error(
          err?.message || err?.msg || "Failed to update favorite on server",
        );
      });
  };

  const hasBookBet = Number(liability) !== 0;
  const fancyPositions = Array.isArray(bookData?.fancy_position)
    ? bookData.fancy_position
    : Array.isArray(bookData?.data?.fancy_position)
      ? bookData.data.fancy_position
      : [];
  const handleBookClick = async () => {
    if (!hasBookBet) return;

    const payload = {
      fancy_id: fancyId ?? id,
    };

    // Remove empty fields to avoid sending undefined/null keys.
    Object.keys(payload).forEach((key) => {
      if (payload[key] == null || payload[key] === "") {
        delete payload[key];
      }
    });

    setBookLoading(true);
    setBookError("");
    setBookData(null);
    setShowBookModal(true);

    try {
      const action = await dispatch(fetchRunPositionAsync(payload));
      if (fetchRunPositionAsync.fulfilled.match(action)) {
        setBookData(action.payload ?? null);
      } else {
        setBookError(action.payload || "Failed to fetch run position");
      }
    } catch (error) {
      setBookError(error?.message || "Failed to fetch run position");
    } finally {
      setBookLoading(false);
    }
  };

  return (
    <div className="fancy-detail-list" key={id}>
      <div>
        <div
          className={`row mx-0 align-items-center tablist-content${
            flash ? " odds-flash-row" : ""
          } ${gameStatus ? "border-right-red-500" : ""}`}
        >
          <div className="col-lg-7 col-md-7 col-7 px-0">
            <div className="d-flex justify-content-between w-full align-items-start">
              <div className="name_of_fancy d-flex align-items-start">
                <a
                  href="#"
                  className="add-pin me-1"
                  aria-label={
                    isFancyBookmarkedState
                      ? "Remove fancy bookmark"
                      : "Bookmark fancy"
                  }
                  aria-pressed={isFancyBookmarkedState}
                  onClick={handleFancyBookmarkClick}
                >
                  <i
                    className={
                      isFancyBookmarkedState
                        ? "bi bi-bookmark-fill"
                        : "bi bi-bookmark"
                    }
                  />
                </a>
                <div className="d-flex flex-column">
                  <span className="marketevent">
                    {" "}
                    {label}
                    {labelSuffix != null && labelSuffix !== "" ? (
                      <b>{labelSuffix}</b>
                    ) : null}{" "}
                  </span>
                </div>
              </div>
              {/* {hasBookBet ? (
                <button
                  type="button"
                  className="btn-cashout px-3 py-1"
                  onClick={handleBookClick}
                >
                  Book
                </button>
              ) : null} */}
              {hasBookBet ? (
                <button
                  type="button"
                  className="btn-cashout rounded-pill px-3 py-1 me-1"
                  onClick={handleBookClick}
                >
                  Book
                </button>
              ) : null}
            </div>
          </div>

          <div className="col-lg-3 col-md-3 col-5 px-0">
            <div className="cs-cls">
              {gameStatus ? (
                <div className="btn-group w-100 match-odds-wrapper flex justify-content-center align-items-center ">
                  <div className="fancy-status-text py-2 text-red-500">
                    {isSuspended ? "SUSPENDED" : gameStatus}
                  </div>
                </div>
              ) : (
                <div className="btn-group">
                  {!backOnly && (
                    <button
                      className="w-100 lay"
                      type="button"
                      onClick={() => {
                        if (!onOddsClick) return;
                        onOddsClick({
                          isFancy: true,
                          fancyId: id,
                          selectionName: label,
                          marketTitle: "Fancy",
                          side: "lay",
                          odds: Number(lay) || 0,
                          minStake: minStake,
                          maxStake: maxStake,
                          runs: Number(lay) || 0,
                          size: Number(laySize) || 0,
                        });
                      }}
                    >
                      <span>{lay}</span>
                      <small>{laySize}</small>
                    </button>
                  )}
                  <button
                    className={`back w-100${backOnly ? "" : ""}`}
                    type="button"
                    onClick={() => {
                      if (!onOddsClick) return;
                      onOddsClick({
                        isFancy: true,
                        fancyId: id,
                        selectionName: label,
                        marketTitle: "Fancy",
                        side: "back",
                        odds: Number(back) || 0,
                        minStake: minStake,
                        maxStake: maxStake,
                        runs: Number(back) || 0,
                        size: Number(backSize) || 0,
                      });
                    }}
                  >
                    <span>{back}</span>
                    <small>{backSize}</small>
                  </button>
                </div>
              )}
            </div>
          </div>
          {gameStatus !== "SUSPENDED" && (
            <div className="col-lg-2 col-md-2 col-4 px-0 d-none d-md-block">
              <button className="two-odds-min-max">
                <span className="d-block">Min Stake: {minStake}</span>
                <span className="d-block">Max Stake: {maxStake}</span>
              </button>
            </div>
          )}
        </div>
        {isSelectedFancyRow && (
          <div className="row mx-0 mt-1 d-lg-none">
            <div className="col-12 px-0">
              <PlaceBetOpenBets inline showOpenBets={false} matchId={matchId} />
            </div>
          </div>
        )}
      </div>
      <Modal
        show={showBookModal}
        onHide={() => setShowBookModal(false)}
        centered
        className="fancy-book-modal"
      >
        <h5 className="card-title">
          Book List
          <button
            type="button"
            className="btn-close"
            aria-label="Close"
            onClick={() => setShowBookModal(false)}
          >
            X
          </button>
        </h5>

        <Modal.Body>
          {bookLoading ? (
            <div className="text-center py-3">Loading...</div>
          ) : bookError ? (
            <div className="text-danger">{bookError}</div>
          ) : fancyPositions.length === 0 ? (
            <div className="text-muted">No data to display</div>
          ) : (
            <div className="table-responsive">
              <table className="table table-sm align-middle mb-0">
                <thead className="border-bottom">
                  <tr>
                    <th className="border-0 pb-2 text-uppercase text-muted small fw-semibold">
                      Runner Name
                    </th>
                    <th className="border-0 pb-2 text-uppercase text-muted small fw-semibold text-end">
                      Profit/Loss
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {fancyPositions.map((row, i) => {
                    const formattedKey = String(row?.key ?? "")
                      .replace(/^0-/, "")
                      .replace(/\+/, "");
                    const value = Number(row?.value) || 0;
                    const valueClass =
                      value > 0
                        ? "text-success"
                        : value < 0
                          ? "text-danger"
                          : "";
                    const valueText =
                      value > 0 ? `+${value.toFixed(2)}` : value.toFixed(2);

                    return (
                      <tr key={`${formattedKey}-${i}`}>
                        <td className="py-2">
                          {formattedKey} {i === 0 ? "Or Less" : "Or More"}
                        </td>
                        <td
                          className={`py-2 text-end fw-semibold ${valueClass}`}
                        >
                          {valueText}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default FancyDetailRow;
